import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Button,
  Link,
  Stack,
  Tooltip,
  IconButton,
  Tabs,
  Tab,
  Checkbox,
  Autocomplete,
  TextField,
  Collapse,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import palette from 'src/theme/palette';
import InputField from 'src/components/InputField';
import LoadingButtonWrapper from 'src/components/LoadingButtonWrapper';
import CustomDropdown from 'src/components/CustomDropdown';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Iconify from 'src/components/Iconify';
import ProjectManagerDropdown from 'src/components/ProjectManagerDropdown';
import MultilineInputField from 'src/components/MultilineInputField';
import JobGroupEditAdd from './JobGroupEditAdd';
import dayjs from 'dayjs';
import { api } from 'src/Api';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const FormContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3)
}));

export default function ProjectBasicForm({ editData }) {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false); // group save button
  const [saveProjectLoading, setSaveProjectLoading] = useState(false); // group save button

  const [showFullForm, setShowFullForm] = useState(false);
  const [value, setValue] = useState(0);

  const [workDaysList, setWorkDaysList] = useState([]);
  const [colorList, setColorList] = useState([]);
  const [projectTypeList, setProjectTypeList] = useState([]);
  const [projectStatusList, setProjectStatusList] = useState([]);
  const [groupList, setGroupList] = useState([]);
  const [projectManagerList, setProjectManagerList] = useState([]);

  const [workDaysError, setWorkDaysError] = useState(false);
  const [scheduleColorError, setScheduleColorError] = useState(false);

  const [jobGroupModal, setJobGroupModal] = useState({ open: false, mode: 'add', value: '' });

  const [selectedManagers, setSelectedManagers] = useState([]);
  const [selectedJobGroup, setSelectedJobGroup] = useState([]);
  const [policyDocuments, setPolicyDocuments] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState([]);

  const [showCustomFields, setShowCustomFields] = useState(true);

  const [form, setForm] = useState({
    title: '',
    type: '',
    status: '',
    projectedStart: null,
    contractType: 'fixed',
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    prefix: '',
    amount: '',
    squareFeet: '',
    permitNumber: '',
    lotInfo: '',
    scheduleProjectedStart: null,
    scheduleActualStart: null,
    scheduleProjectedCompletion: null,
    scheduleActualCompletion: null,
    updateActualDates: false,
    notifyProjects: false,
    scheduleColor: null,
    workDays: [],
    notesInternal: '',
    notesVendors: '',
    claimInformation: '',
    policyDocument: null,
    permitParcelId: '',
    contractorLicense: '',
    flGisLink: '',
    localSupplierLink: '',
    propertySpecialNotes: ''
  });

  useEffect(() => {
    if (editData && editData.id) {
      GetProjectDetailId();
    }
  }, [editData]);

  useEffect(() => {
    if (detailData) {
      setForm((prev) => ({
        ...prev,
        title: detailData.name || '',
        type: detailData.project_type_id || '',
        status: detailData.project_status_id || '',
        projectedStart: detailData.project_start_date ? dayjs(detailData.project_start_date) : null,
        contractType: detailData.contract_type_id == 2 ? 'open' : 'fixed',
        streetAddress: detailData.address || '',
        city: detailData.city || '',
        state: detailData.state || '',
        zipCode: detailData.zip_code || '',
        prefix: detailData.prefix || '',
        amount: detailData.contract_price || '',
        squareFeet: detailData.square_feet || '',
        permitNumber: detailData.permit_number || '',
        lotInfo: detailData.log_info || '',
        scheduleProjectedStart: detailData.project_start_date
          ? dayjs(detailData.project_start_date)
          : null,
        scheduleActualStart: detailData.project_actual_start_date
          ? dayjs(detailData.project_actual_start_date)
          : null,
        scheduleProjectedCompletion: detailData.project_completion_date
          ? dayjs(detailData.project_completion_date)
          : null,
        scheduleActualCompletion: detailData.project_actual_completion_date
          ? dayjs(detailData.project_actual_completion_date)
          : null,
        updateActualDates: detailData.update_actual_date == 1 ? true : false,
        notifyProjects: detailData.project_managers_notify == 1 ? true : false,
        scheduleColor: detailData?.color?.id
          ? {
              value: detailData.color.id,
              name: detailData.color.name,
              color: detailData.color.code
            }
          : null,

        workDays: Array.isArray(detailData.day) ? detailData.day.map((d) => d.id) : [],
        notesInternal: detailData.notes_for_internal_users || '',
        notesVendors: detailData.notes_for_sub_vendor || '',
        claimInformation: detailData.claim_information || '',
        policyDocument: null, // You can handle file fetch/display if needed
        permitParcelId: detailData.permit_parcel_id || '',
        contractorLicense: detailData.contractor_license || '',
        flGisLink: detailData.fl_gis_links || '',
        localSupplierLink: detailData.local_supplier_link || '',
        propertySpecialNotes: detailData.property_special_notes || ''
      }));
      if (editData?.id) {
        setShowFullForm(detailData.is_advance == 1 ? true : false);
      }

      setSelectedManagers(
        Array.isArray(detailData.managers) ? detailData.managers.map((m) => m.id) : []
      );
      setSelectedJobGroup(
        Array.isArray(detailData.project_group) ? detailData.project_group.map((g) => g.id) : []
      );

      if (detailData?.policy_documents && Array.isArray(detailData.policy_documents)) {
        setPolicyDocuments(
          detailData.policy_documents.map((doc) => ({
            id: doc.id,
            name: doc.file_name,
            url: doc.file_url
          }))
        );
      }
    }
  }, [detailData]);

  //DROPDOWN APIS
  useEffect(() => {
    GetWorkDayData();
    GetColorData();
    GetProjectType();
    GetStatusList();
    GetGroupList();
    GetProjectManagerList();
  }, []);

  // GET PROJECT DETAILS BY ID
  function GetProjectDetailId() {
    setDetailLoading(true);
    const myHeaders = new Headers();
    myHeaders.append('Authorization', `Bearer ${token}`);

    const queryParams = new URLSearchParams();
    queryParams.append('id', editData.id);

    const requestOptions = {
      method: 'GET',
      redirect: 'follow',
      headers: myHeaders
    };

    fetch(`${api.get_project_by_id}?${queryParams}`, requestOptions)
      .then((response) => {
        if (response.status === 200) {
          return response.text();
        } else {
          throw new Error('Failed to fetch Deal data');
        }
      })
      .then((result_) => {
        const result = JSON.parse(result_);
        setDetailData(result?.data);
      })
      .catch((error) => {
        console.log('error', error);
      })
      .finally(() => {
        setDetailLoading(false);
      });
  }

  // GET WORK DAYS
  function GetWorkDayData() {
    const myHeaders = new Headers();
    myHeaders.append('Authorization', `Bearer ${token}`);

    const requestOptions = {
      method: 'GET',
      redirect: 'follow',
      headers: myHeaders
    };

    fetch(api.get_working_day, requestOptions)
      .then((response) => {
        if (response.status === 200) {
          return response.text();
        } else {
          throw new Error('Failed to fetch Deal data');
        }
      })
      .then((result_) => {
        const result = JSON.parse(result_);
        setWorkDaysList(result?.data);
      })
      .catch((error) => {
        console.log('error', error);
      })
      .finally(() => {});
  }

  // GET COLOR LIST
  function GetColorData() {
    const myHeaders = new Headers();
    myHeaders.append('Authorization', `Bearer ${token}`);

    const requestOptions = {
      method: 'GET',
      redirect: 'follow',
      headers: myHeaders
    };

    fetch(api.get_all_colors, requestOptions)
      .then((response) => {
        if (response.status === 200) {
          return response.text();
        } else {
          throw new Error('Failed to fetch Deal data');
        }
      })
      .then((result_) => {
        const result = JSON.parse(result_);
        setColorList(result?.data);
      })
      .catch((error) => {
        console.log('error', error);
      })
      .finally(() => {});
  }

  // GET PROJECT TYPES
  function GetProjectType() {
    const myHeaders = new Headers();
    myHeaders.append('Authorization', `Bearer ${token}`);

    const requestOptions = {
      method: 'GET',
      redirect: 'follow',
      headers: myHeaders
    };

    fetch(api.get_project_type, requestOptions)
      .then((response) => {
        if (response.status === 200) {
          return response.text();
        } else {
          throw new Error('Failed to fetch Deal data');
        }
      })
      .then((result_) => {
        const result = JSON.parse(result_);
        setProjectTypeList(result?.data);
      })
      .catch((error) => {
        console.log('error', error);
      })
      .finally(() => {});
  }

  // GET STATUS LIST
  function GetStatusList() {
    const myHeaders = new Headers();
    myHeaders.append('Authorization', `Bearer ${token}`);

    const requestOptions = {
      method: 'GET',
      redirect: 'follow',
      headers: myHeaders
    };

    fetch(api.get_project_type_statuses, requestOptions)
      .then((response) => {
        if (response.status === 200) {
          return response.text();
        } else {
          throw new Error('Failed to fetch Deal data');
        }
      })
      .then((result_) => {
        const result = JSON.parse(result_);
        setProjectStatusList(result?.data);
      })
      .catch((error) => {
        console.log('error', error);
      })
      .finally(() => {});
  }

  // GET GROUP LIST
  function GetGroupList() {
    const myHeaders = new Headers();
    myHeaders.append('Authorization', `Bearer ${token}`);

    const requestOptions = {
      method: 'GET',
      redirect: 'follow',
      headers: myHeaders
    };

    fetch(api.get_group, requestOptions)
      .then((response) => {
        if (response.status === 200) {
          return response.text();
        } else {
          throw new Error('Failed to fetch Deal data');
        }
      })
      .then((result_) => {
        const result = JSON.parse(result_);
        setGroupList(result?.data);
      })
      .catch((error) => {
        console.log('error', error);
      })
      .finally(() => {});
  }

  // GET GROUP LIST
  function GetProjectManagerList() {
    const myHeaders = new Headers();
    myHeaders.append('Authorization', `Bearer ${token}`);

    const requestOptions = {
      method: 'GET',
      redirect: 'follow',
      headers: myHeaders
    };

    fetch(api.get_project_manager, requestOptions)
      .then((response) => {
        if (response.status === 200) {
          return response.text();
        } else {
          throw new Error('Failed to fetch Deal data');
        }
      })
      .then((result_) => {
        const result = JSON.parse(result_);
        setProjectManagerList(result?.data);
      })
      .catch((error) => {
        console.log('error', error);
      })
      .finally(() => {});
  }

  // HANDLE ADD/EDIT JOB GROUP
  function handleJobGroupSave({ id, name }) {
    setSaveLoading(true);
    const token = localStorage.getItem('token');
    const myHeaders = new Headers();
    myHeaders.append('Authorization', `Bearer ${token}`);

    const formData = new FormData();
    name && formData.append('name', name);
    id && formData.append('id', id);

    const requestOptions = {
      method: 'POST',
      redirect: 'follow',
      headers: myHeaders,
      body: formData
    };

    fetch(api.add_edit_group, requestOptions)
      .then((response) =>
        response.text().then((result) => ({ status: response.status, body: result }))
      )
      .then(({ status, body }) => {
        const result = JSON.parse(body);
        if (status === 200 || status === 201) {
          toast.success(result.message);
          GetGroupList();
          if (jobGroupModal.mode !== 'add') {
            setSelectedJobGroup([id]);
          }
          setJobGroupModal({ ...jobGroupModal, open: false });
        } else {
          toast.error(result.error);
        }
      })
      .catch((error) => {
        toast.error(error.error);
        console.log('error', error);
      })
      .finally(() => {
        setSaveLoading(false);
      });
  }

  // Work days handler (ProjectManagerDropdown style)
  const handleWorkDaysDropdownChange = (e) => {
    const value = e.target.value;
    setForm((prev) => ({
      ...prev,
      workDays: value
    }));
    setWorkDaysError(value.length === 0);
  };

  // Handler for file upload
  const handlePolicyDocumentChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadingFiles((prev) => [...prev, file.name]);
      handleUploadDocument(file);
    }
  };

  // Handler for schedule color (single select)
  const handleScheduleColorAutocomplete = (event, value) => {
    setForm((prev) => ({
      ...prev,
      scheduleColor: value // value ya null
    }));
    setScheduleColorError(!value);
  };

  // Universal handleChange for all fields
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // For dropdowns that don't provide name in event
  const handleDropdownChange = (name, value) => {
    setForm((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  // For number fields with validation
  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      setForm((prev) => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // For lot info (multiline)
  const handleLotInfoChange = (e) => {
    setForm((prev) => ({
      ...prev,
      lotInfo: e.target.value
    }));
  };

  // For date picker
  const handleDateChange = (newValue) => {
    setForm((prev) => ({
      ...prev,
      projectedStart: newValue
    }));
  };

  // Schedule date change handler
  const handleScheduleDateChange = (name, value) => {
    setForm((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  // Checkbox handler (update actual dates)
  const handleCheckboxChange = (e) => {
    const checked = e.target.checked;
    setForm((prev) => ({
      ...prev,
      updateActualDates: checked,
      // If checked, clear actual start/completion
      scheduleActualStart: checked ? null : prev.scheduleActualStart,
      scheduleActualCompletion: checked ? null : prev.scheduleActualCompletion
    }));
  };

  // Checkbox handler (update actual dates)
  const handleCheckboxChangeNotify = (e) => {
    const checked = e.target.checked;
    setForm((prev) => ({
      ...prev,
      notifyProjects: checked
    }));
  };

  // Tab change handler
  const handleChangeTab = (event, newValue) => {
    setValue(newValue);
  };

  // Validation for schedule dates
  const projectedCompletionError =
    !form.updateActualDates &&
    form.scheduleProjectedStart &&
    form.scheduleProjectedCompletion &&
    dayjs(form.scheduleProjectedCompletion).isBefore(dayjs(form.scheduleProjectedStart));

  const actualCompletionError =
    !form.updateActualDates &&
    form.scheduleProjectedStart &&
    form.scheduleActualCompletion &&
    dayjs(form.scheduleActualCompletion).isBefore(dayjs(form.scheduleProjectedStart));

  // HANLDE SAVE PROJECT FUNCTION
  function handleSave() {
    setSaveProjectLoading(true);
    const token = localStorage.getItem('token');
    const myHeaders = new Headers();
    myHeaders.append('Authorization', `Bearer ${token}`);

    const formData = new FormData();
    editData?.id && formData.append('id', editData?.id);
    form?.title && formData.append('name', form?.title);
    form?.prefix && formData.append('prefix', form?.prefix);
    form?.type && formData.append('project_type_id', form?.type);
    form?.amount && formData.append('contract_price', form?.amount);
    if (selectedManagers.length > 0) {
      formData.append('project_managers', selectedManagers.join(','));
      formData.append('project_managers_notify', form?.notifyProjects ? 1 : 0);
    }
    form?.squareFeet && formData.append('square_feet', form?.squareFeet);
    form?.permitNumber && formData.append('permit_number', form?.permitNumber);
    form?.lotInfo && formData.append('log_info', form?.lotInfo);
    if (selectedJobGroup.length > 0) {
      formData.append('project_group_id', selectedJobGroup.join(','));
    }
    formData.append('contract_type_id', form?.contractType === 'fixed' ? 1 : 2);
    form?.status && formData.append('project_status_id', form?.status);
    form?.streetAddress && formData.append('address', form?.streetAddress);
    form?.city && formData.append('city', form?.city);
    form?.state && formData.append('state', form?.state);
    form?.zipCode && formData.append('zip_code', form?.zipCode);
    !showFullForm &&
      form?.projectedStart &&
      formData.append('project_start_date', dayjs(form?.projectedStart).format('YYYY-MM-DD'));
    showFullForm &&
      form?.scheduleProjectedStart &&
      formData.append(
        'project_start_date',
        dayjs(form?.scheduleProjectedStart).format('YYYY-MM-DD')
      );
    !form?.updateActualDates &&
      form?.scheduleActualStart &&
      formData.append(
        'project_actual_start_date',
        dayjs(form?.scheduleActualStart).format('YYYY-MM-DD')
      );
    form?.scheduleProjectedCompletion &&
      formData.append(
        'project_completion_date',
        dayjs(form?.scheduleProjectedCompletion).format('YYYY-MM-DD')
      );
    !form?.updateActualDates &&
      form?.scheduleActualCompletion &&
      formData.append(
        'project_actual_completion_date',
        dayjs(form?.scheduleActualCompletion).format('YYYY-MM-DD')
      );
    showFullForm && formData.append('update_actual_date', form?.updateActualDates ? 1 : 0);
    // form?.scheduleColor.value && formData.append('color_id', form?.scheduleColor.value);
    if (form.scheduleColor && form.scheduleColor.value) {
      formData.append('color_id', form.scheduleColor.value);
    }
    form?.workDays.length > 0 && formData.append('project_work_day_id', form?.workDays.join(','));
    form?.notesInternal && formData.append('notes_for_internal_users', form?.notesInternal);
    form?.notesVendors && formData.append('notes_for_sub_vendor', form?.notesVendors);
    form?.claimInformation && formData.append('claim_information', form?.claimInformation);
    form?.permitParcelId && formData.append('permit_parcel_id', form?.permitParcelId);
    form?.contractorLicense && formData.append('contractor_license', form?.contractorLicense);
    form?.flGisLink && formData.append('fl_gis_links', form?.flGisLink);
    form?.localSupplierLink && formData.append('local_supplier_link', form?.localSupplierLink);
    form?.propertySpecialNotes &&
      formData.append('property_special_notes', form?.propertySpecialNotes);
    if (policyDocuments.length > 0) {
      formData.append('policy_documents', policyDocuments.map((doc) => doc.id).join(','));
    }
    formData.append('is_advance', showFullForm ? 1 : 0);

    const requestOptions = {
      method: 'POST',
      redirect: 'follow',
      headers: myHeaders,
      body: formData
    };

    fetch(api.add_edit_project, requestOptions)
      .then((response) =>
        response.text().then((result) => ({ status: response.status, body: result }))
      )
      .then(({ status, body }) => {
        const result = JSON.parse(body);
        if (status === 200 || status === 201) {
          navigate(-1);
          toast.success(result.message);
          setForm({
            title: '',
            type: '',
            status: '',
            projectedStart: null,
            contractType: 'fixed',
            streetAddress: '',
            city: '',
            state: '',
            zipCode: '',
            prefix: '',
            amount: '',
            squareFeet: '',
            permitNumber: '',
            lotInfo: '',
            scheduleProjectedStart: null,
            scheduleActualStart: null,
            scheduleProjectedCompletion: null,
            scheduleActualCompletion: null,
            updateActualDates: false,
            notifyProjects: false,
            scheduleColor: null,
            workDays: [],
            notesInternal: '',
            notesVendors: '',
            claimInformation: '',
            policyDocument: null,
            permitParcelId: '',
            contractorLicense: '',
            flGisLink: '',
            localSupplierLink: '',
            propertySpecialNotes: ''
          });
          setSelectedManagers([]);
          setSelectedJobGroup([]);
          setPolicyDocuments([]);
        } else {
          toast.error(result.error);
        }
      })
      .catch((error) => {
        toast.error(error.error);
        console.log('error', error);
      })
      .finally(() => {
        setSaveProjectLoading(false);
      });
  }

  // HANLDE UPLOAD DOCUMENT FUNCTION
  function handleUploadDocument(file) {
    setPolicyDocuments((prev) => [
      ...prev,
      {
        id: `temp-${file.name}`, // temporary id
        name: file.name,
        url: '', // uploading, so no url yet
        uploading: true
      }
    ]);
    const token = localStorage.getItem('token');
    const myHeaders = new Headers();
    myHeaders.append('Authorization', `Bearer ${token}`);

    const formData = new FormData();
    formData.append('policy_documents', file);

    const requestOptions = {
      method: 'POST',
      redirect: 'follow',
      headers: myHeaders,
      body: formData
    };

    fetch(api.upload_policy_documents, requestOptions)
      .then((response) =>
        response.text().then((result) => ({ status: response.status, body: result }))
      )
      .then(({ status, body }) => {
        const result = JSON.parse(body);
        if (status === 200 || status === 201) {
          toast.success(result.message);
          setPolicyDocuments((prev) =>
            prev
              .filter((doc) => doc.id !== `temp-${file.name}`)
              .concat({
                id: result?.data?.id,
                name: file.name,
                url: result?.data?.file_url || '',
                uploading: false
              })
          );
        } else {
          toast.error(result.error);
          setPolicyDocuments((prev) => prev.filter((doc) => doc.id !== `temp-${file.name}`));
        }
      })
      .catch((error) => {
        toast.error(error.error);
        setPolicyDocuments((prev) => prev.filter((doc) => doc.id !== `temp-${file.name}`));
        console.log('error', error);
      })
      .finally(() => {
        setUploadingFiles((prev) => prev.filter((n) => n !== file.name));
      });
  }

  const handleDeletePolicyDocument = (id) => {
    setPolicyDocuments((prev) => prev.filter((doc) => doc.id !== id));
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {detailLoading ? (
        <Box
          sx={{
            minHeight: 600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <CircularProgress size={48} />
        </Box>
      ) : (
        <>
          {showFullForm && (
            <Box
              sx={{
                px: 2,
                mt: -1,
                mb: -1,
                borderBottom: 1,
                pb: 1,
                borderColor: palette.primary.lightMain
              }}
            >
              <Tabs
                value={value}
                onChange={handleChangeTab}
                textColor="primary"
                TabIndicatorProps={{ style: { display: 'none' } }} // hide underline
                aria-label="account tabs"
                sx={{
                  minHeight: 30 // Tabs container ki height bhi kam
                }}
              >
                <Tab
                  label="Job Details"
                  sx={{
                    minHeight: 30, // Tab ki height kam
                    padding: '4px 10px', // upar niche kam spacing
                    fontFamily: value === 0 ? 'poppinsBold' : 'poppinsLight',
                    color: value === 0 ? palette.primary.white : palette.primary.disabled,
                    fontSize: 12,
                    backgroundColor:
                      value === 0 ? palette.primary.lightMain : palette.primary.inputborder,
                    borderRadius: '6px',
                    mr: 1
                  }}
                />
                {/* <Tab
              label="Map"
              sx={{
                minHeight: 35, // Tab ki height kam
                padding: '4px 10px', // upar niche kam spacing
                fontFamily: value === 1 ? 'poppinsBold' : 'poppinsLight',
                color: value === 1 ? palette.primary.white : palette.primary.disabled,
                fontSize: 13,
                backgroundColor:
                  value === 1 ? palette.primary.lightMain : palette.primary.inputborder,
                borderRadius: '6px'
              }}
            /> */}
              </Tabs>
            </Box>
          )}

          {/* FIRST FORM PART */}
          {!showFullForm && (
            <FormContainer sx={{ pl: 2, pr: 2 }}>
              <Grid container>
                <Grid item xs={12} md={12} sx={{ ml: { xs: 0, sm: 0 } }}>
                  <form>
                    <Grid container spacing={2}>
                      {/* Title */}
                      <Grid item xs={12} sx={{ marginBottom: -2 }}>
                        <InputField
                          name="title"
                          label="Title"
                          //   placeholder="Enter title"
                          backgroundColor="#fff"
                          labelFontMedium
                          value={form.title}
                          onChange={handleChange}
                          required
                        />
                      </Grid>

                      {/* Type  */}
                      <Grid item xs={12} md={6}>
                        <CustomDropdown
                          label="Type"
                          marginBottom={'0rem'}
                          value={form.type}
                          height={38}
                          onChange={(e) => handleDropdownChange('type', e.target.value)}
                          options={projectTypeList.map((val) => ({
                            value: val?.id,
                            label: val?.name
                          }))}
                          fontFamily="poppinsRegular"
                          backgroundColor="#fff"
                          labelFontMedium
                        />
                      </Grid>

                      {/* Projected Start */}
                      <Grid item xs={12} md={6}>
                        <Typography
                          sx={{
                            fontSize: 12,
                            marginBottom: '-4px',
                            fontFamily: 'poppinsMedium',
                            color: '#1a1a1a'
                          }}
                        >
                          {'Projected start'}
                        </Typography>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DemoContainer components={['DatePicker', 'DatePicker', 'DatePicker']}>
                            <DatePicker
                              value={form.projectedStart} // Correctly set value for month picker
                              onChange={handleDateChange}
                              label={''}
                              slotProps={{
                                textField: {
                                  size: 'small',
                                  fullWidth: true,
                                  error: '',
                                  placeholder: '',
                                  sx: {
                                    backgroundColor: '#fff',
                                    borderRadius: '7px',
                                    '& .MuiInputBase-input': {
                                      fontSize: 11,
                                      fontFamily: 'poppinsMedium',
                                      padding: '11px 12px'
                                    },
                                    '& .MuiOutlinedInput-root': {
                                      '& fieldset': {
                                        borderColor: palette.primary.inputborder,
                                        borderWidth: '1.5px',
                                        borderRadius: '7px'
                                      },
                                      '&:hover fieldset': {
                                        borderColor: palette.primary.inputborder
                                      },
                                      '&.Mui-focused fieldset': {
                                        borderColor: palette.primary.inputborder
                                      }
                                    }
                                  }
                                },
                                openPickerIcon: {
                                  sx: { fontSize: 18, color: palette.primary.dropdownArrow }
                                }
                              }}
                              slots={{
                                openPickerIcon: CalendarMonthIcon
                              }}
                            />
                          </DemoContainer>
                        </LocalizationProvider>
                      </Grid>

                      {/* Contract type Heading */}
                      <Stack
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'center',
                          alignItems: 'center',
                          mt: 2,
                          ml: 2
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 13,
                            fontFamily: 'poppinsBold',
                            color: '#1a1a1a'
                          }}
                        >
                          {'Contract type'}
                        </Typography>
                        <Tooltip title="Buildertrend will create workflows to best fit your financial structure.">
                          <IconButton>
                            <Iconify
                              icon={'material-symbols:info-rounded'}
                              width={18}
                              height={18}
                              style={{ cursor: 'pointer', color: palette.primary.main }}
                            />
                          </IconButton>
                        </Tooltip>
                      </Stack>

                      {/* Contract Type - Radio */}
                      <Grid item xs={12} sx={{ marginTop: -1 }}>
                        <FormControl>
                          {/* Radio Buttons */}
                          <RadioGroup
                            row
                            name="contractType"
                            value={form.contractType}
                            onChange={handleChange}
                            sx={{ display: 'flex', flexDirection: 'row' }} // spacing between options
                          >
                            {/* Fixed Price Option */}
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                mr: 4
                              }}
                            >
                              <Box
                                sx={{ display: 'flex', alignItems: 'center', ml: -1.3, mb: -0.5 }}
                              >
                                <Radio value="fixed" checked={form.contractType === 'fixed'} />
                                <Typography
                                  sx={{
                                    fontSize: 14,
                                    fontFamily: 'poppinsSemiBold'
                                  }}
                                >
                                  Fixed price
                                </Typography>
                              </Box>
                              <Typography
                                sx={{ fontSize: 11, fontFamily: 'poppinsRegular', ml: 4 }}
                              >
                                You will set the contract price for the client
                              </Typography>
                            </Box>

                            {/* Open Book Option */}
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  ml: { xs: -1.3, sm: -1.3, md: -1.3 },
                                  mb: -0.5
                                }}
                              >
                                <Radio value="open" checked={form.contractType === 'open'} />
                                <Typography sx={{ fontSize: 14, fontFamily: 'poppinsSemiBold' }}>
                                  Open book
                                </Typography>
                              </Box>
                              <Typography
                                sx={{
                                  fontSize: 11,
                                  fontFamily: 'poppinsRegular',
                                  ml: { xs: 4, sm: 4, md: 4 }
                                }}
                              >
                                Price will be determined by your actual costs plus markup/margin
                                (includes Cost Plus and Time & Materials contracts)
                              </Typography>
                            </Box>
                          </RadioGroup>
                        </FormControl>
                      </Grid>

                      {/* Street Address */}
                      <Grid item xs={12}>
                        <InputField
                          name="streetAddress"
                          label="Street address"
                          backgroundColor="#fff"
                          value={form.streetAddress}
                          onChange={handleChange}
                          labelFontMedium
                        />
                      </Grid>

                      {/* City, State, Zip */}
                      <Grid item xs={12} sm={6} sx={{ marginTop: -1 }}>
                        <InputField
                          name="city"
                          label="City"
                          backgroundColor="#fff"
                          value={form.city}
                          onChange={handleChange}
                          labelFontMedium
                        />
                      </Grid>
                      <Grid item xs={12} sm={3} sx={{ marginTop: -1 }}>
                        <InputField
                          name="state"
                          label="State"
                          backgroundColor="#fff"
                          value={form.state}
                          onChange={handleChange}
                          labelFontMedium
                        />
                      </Grid>
                      <Grid item xs={12} sm={3} sx={{ marginTop: -1 }}>
                        <InputField
                          name="zipCode"
                          label="Zip code"
                          backgroundColor="#fff"
                          value={form.zipCode}
                          onChange={(e) => {
                            const onlyNumbers = e.target.value.replace(/\D/g, '');
                            setForm((prev) => ({ ...prev, zipCode: onlyNumbers }));
                          }}
                          labelFontMedium
                        />
                      </Grid>

                      {/* More Options */}
                      <Grid item xs={12} sx={{ marginTop: -2 }}>
                        <Link
                          component="button"
                          underline="hover"
                          sx={{ fontSize: 13, fontFamily: 'poppinsMedium' }}
                          onClick={() => setShowFullForm(true)}
                        >
                          More options
                        </Link>
                      </Grid>
                    </Grid>
                  </form>
                </Grid>
              </Grid>
            </FormContainer>
          )}

          {/* FULL FORM  */}
          {showFullForm && (
            <FormContainer sx={{ pl: 2, pr: 2 }}>
              <form>
                <Grid container sx={{ display: 'flex', flexDirection: 'row' }}>
                  {/* LEFT SIDE  */}
                  <Grid item xs={12} md={6} sx={{ mr: { xs: 0, md: 7 } }}>
                    {/* FIRST ROW */}
                    <Grid
                      container
                      spacing={2}
                      sx={{ display: 'flex', flexDirection: 'row', marginBottom: -2 }}
                    >
                      {/* Title */}
                      <Grid item xs={12} md={8}>
                        <InputField
                          name="title"
                          label="Title"
                          backgroundColor="#fff"
                          value={form.title}
                          onChange={handleChange}
                          required
                          marginBottom={'0rem'}
                          labelFontMedium
                        />
                      </Grid>
                      {/* Prefix */}
                      <Grid item xs={12} md={4}>
                        <InputField
                          name="prefix"
                          label="Prefix"
                          backgroundColor="#fff"
                          value={form.prefix}
                          onChange={handleChange}
                          labelFontMedium
                        />
                      </Grid>
                    </Grid>

                    {/* TYPE */}
                    <Grid item xs={12} md={7.9} sx={{ marginTop: 2 }}>
                      <CustomDropdown
                        label="Type"
                        marginBottom={'0rem'}
                        value={form.type}
                        height={38}
                        onChange={(e) => handleDropdownChange('type', e.target.value)}
                        options={projectTypeList.map((val) => ({
                          value: val?.id,
                          label: val?.name
                        }))}
                        fontFamily="poppinsRegular"
                        backgroundColor="#fff"
                        labelFontMedium
                      />
                    </Grid>

                    {/* CONTRACT TYPE HEADING */}
                    <Stack
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        mt: 1
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontFamily: 'poppinsBold',
                          color: '#1a1a1a'
                        }}
                      >
                        {'Contract type'}
                      </Typography>
                      <Tooltip title="Buildertrend will create workflows to best fit your financial structure.">
                        <IconButton>
                          <Iconify
                            icon={'material-symbols:info-rounded'}
                            width={18}
                            height={18}
                            style={{ cursor: 'pointer', color: palette.primary.main }}
                          />
                        </IconButton>
                      </Tooltip>
                    </Stack>

                    {/* CONTRACT TYPE - RADIO */}
                    <Grid item xs={12} sx={{ marginTop: -1 }}>
                      <FormControl>
                        {/* Radio Buttons */}
                        <RadioGroup
                          row
                          name="contractType"
                          value={form.contractType}
                          onChange={handleChange}
                          sx={{ display: 'flex', flexDirection: 'row' }} // spacing between options
                        >
                          {/* Fixed Price Option */}
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              mr: 4
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', ml: -1.3, mb: -0.5 }}>
                              <Radio value="fixed" checked={form.contractType === 'fixed'} />
                              <Typography
                                sx={{
                                  fontSize: 14,
                                  fontFamily: 'poppinsSemiBold'
                                }}
                              >
                                Fixed price
                              </Typography>
                            </Box>
                            <Typography sx={{ fontSize: 11, fontFamily: 'poppinsRegular', ml: 4 }}>
                              You will set the contract price for the client
                            </Typography>
                          </Box>

                          {/* Open Book Option */}
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                ml: { xs: -1.3, sm: -1.3, md: -1.3 },
                                mb: -0.5
                              }}
                            >
                              <Radio value="open" checked={form.contractType === 'open'} />
                              <Typography sx={{ fontSize: 14, fontFamily: 'poppinsSemiBold' }}>
                                Open book
                              </Typography>
                            </Box>
                            <Typography
                              sx={{
                                fontSize: 11,
                                fontFamily: 'poppinsRegular',
                                ml: { xs: 4, sm: 4, md: 4 }
                              }}
                            >
                              Price will be determined by your actual costs plus markup/margin
                              (includes Cost Plus and Time & Materials contracts)
                            </Typography>
                          </Box>
                        </RadioGroup>
                      </FormControl>
                    </Grid>

                    {/* JOB GROUP DROPDOWN WITH ADD/EDIT BUTTONS */}
                    <Grid item xs={12} md={12} sx={{ mt: 2 }}>
                      <Grid item xs={12} md={12}>
                        <ProjectManagerDropdown
                          label="Job group"
                          name="job_group"
                          value={selectedJobGroup} // Always an array of IDs
                          onChange={(e) => setSelectedJobGroup(e.target.value)}
                          showNameTag={false}
                          options={groupList.map((val) => ({
                            value: val?.id,
                            name: val?.name,
                            code: val?.name
                          }))}
                          error={false}
                          helperText=""
                          placeholder=""
                          labelFontMedium
                        />
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        md={12}
                        sx={{
                          mt: { xs: 0, md: 2 },
                          mb: { xs: 1 },
                          display: 'flex',
                          justifyContent: 'flex-end'
                        }}
                      >
                        <LoadingButtonWrapper
                          title="Add"
                          style={{
                            width: 60,
                            borderRadius: 5,
                            letterSpacing: 0.5,
                            boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.1)',
                            transition: 'width 0.3s ease',
                            marginRight: 10
                          }}
                          height={30}
                          fontSize={12}
                          fontFamily="poppinsLight"
                          onClick={() => setJobGroupModal({ open: true, mode: 'add', value: '' })}
                        />

                        <LoadingButtonWrapper
                          disabled={selectedJobGroup?.length === 1 ? false : true}
                          title="Edit"
                          style={{
                            width: 60,
                            borderRadius: 5,
                            letterSpacing: 0.5,
                            boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.1)',
                            transition: 'width 0.3s ease'
                          }}
                          height={30}
                          fontSize={12}
                          fontFamily="poppinsLight"
                          onClick={() => {
                            const selectedId = selectedJobGroup[0];
                            const selectedGroup = groupList.find((g) => g.id === selectedId);
                            setJobGroupModal({
                              open: true,
                              mode: 'edit',
                              value: {
                                id: selectedGroup?.id,
                                name: selectedGroup?.name || ''
                              }
                            });
                          }}
                        />
                      </Grid>
                    </Grid>

                    {/*  ADDRESS HEADING */}
                    <Typography
                      sx={{
                        fontSize: 20,
                        fontFamily: 'poppinsSemiBold'
                      }}
                    >
                      Address
                    </Typography>

                    {/* STREET ADDRESS */}
                    <Grid item xs={12} sx={{ mt: 1 }}>
                      <InputField
                        name="streetAddress"
                        label="Street address"
                        placeholder=""
                        backgroundColor="#fff"
                        value={form.streetAddress}
                        onChange={handleChange}
                        labelFontMedium
                      />
                    </Grid>

                    {/* CITY, STATE, ZIP */}
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <InputField
                          name="city"
                          label="City"
                          placeholder=""
                          backgroundColor="#fff"
                          value={form.city}
                          onChange={handleChange}
                          marginBottom={'0rem'}
                          labelFontMedium
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <InputField
                          name="state"
                          label="State"
                          backgroundColor="#fff"
                          value={form.state}
                          onChange={handleChange}
                          marginBottom={'0rem'}
                          labelFontMedium
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <InputField
                          name="zipCode"
                          label="Zip code"
                          backgroundColor="#fff"
                          value={form.zipCode}
                          onChange={(e) => {
                            const onlyNumbers = e.target.value.replace(/\D/g, '');
                            setForm((prev) => ({ ...prev, zipCode: onlyNumbers }));
                          }}
                          labelFontMedium
                        />
                      </Grid>
                    </Grid>

                    {/*  SCHEDULE HEADING */}
                    <Typography
                      sx={{
                        fontSize: 20,
                        fontFamily: 'poppinsSemiBold'
                      }}
                    >
                      Schedule
                    </Typography>

                    {/* SCHEDULE DATES */}
                    <Grid container xs={12} sx={{ mb: { xs: 0, sm: 0 }, mt: 0.5 }}>
                      {/* PROJECT START */}
                      <Grid item xs={12} sm={6} md={6} sx={{ mb: { xs: 2, sm: 2 } }}>
                        <Typography
                          sx={{
                            fontSize: 12,
                            marginBottom: '-4px',
                            fontFamily: 'poppinsMedium',
                            color: palette.primary.titledark
                          }}
                        >
                          {'Projected start'}
                        </Typography>
                        <Box
                          sx={{
                            minHeight: 38,
                            display: 'flex',
                            alignItems: 'center',
                            fontFamily: 'poppinsSemiBold',
                            fontSize: 15,
                            color: palette.primary.titledark
                          }}
                        >
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DemoContainer components={['DatePicker', 'DatePicker', 'DatePicker']}>
                              <DatePicker
                                value={form.scheduleProjectedStart}
                                onChange={(val) =>
                                  handleScheduleDateChange('scheduleProjectedStart', val)
                                }
                                label={''}
                                slotProps={{
                                  textField: {
                                    size: 'small',
                                    fullWidth: true,
                                    error: '',
                                    placeholder: '',
                                    sx: {
                                      backgroundColor: '#fff',
                                      borderRadius: '7px',
                                      '& .MuiInputBase-input': {
                                        fontSize: 11,
                                        fontFamily: 'poppinsSemiBold',
                                        padding: '11px 12px'
                                      },
                                      '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                          borderColor: palette.primary.inputborder,
                                          borderWidth: '1.5px',
                                          borderRadius: '7px'
                                        },
                                        '&:hover fieldset': {
                                          borderColor: palette.primary.inputborder
                                        },
                                        '&.Mui-focused fieldset': {
                                          borderColor: palette.primary.inputborder
                                        }
                                      }
                                    }
                                  },
                                  openPickerIcon: {
                                    sx: { fontSize: 18, color: palette.primary.dropdownArrow }
                                  }
                                }}
                                slots={{
                                  openPickerIcon: CalendarMonthIcon
                                }}
                              />
                            </DemoContainer>
                          </LocalizationProvider>
                        </Box>
                      </Grid>

                      {/* ACTUAL START */}
                      <Grid item xs={12} sm={6} md={6} sx={{ mb: { xs: 2, sm: 2 } }}>
                        <Typography
                          sx={{
                            fontSize: 12,
                            marginBottom: '-4px',
                            fontFamily: 'poppinsMedium',
                            color: palette.primary.titledark
                          }}
                        >
                          {'Actual start'}
                        </Typography>
                        <Box
                          sx={{
                            minHeight: 38,
                            display: 'flex',
                            alignItems: 'center',
                            fontFamily: 'poppinsSemiBold',
                            fontSize: 15,
                            color: palette.primary.titledark,
                            paddingTop: form.updateActualDates ? 1.5 : 0
                          }}
                        >
                          {form.updateActualDates ? (
                            '--'
                          ) : (
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <DemoContainer
                                components={['DatePicker', 'DatePicker', 'DatePicker']}
                              >
                                <DatePicker
                                  value={form.scheduleActualStart}
                                  onChange={(val) =>
                                    handleScheduleDateChange('scheduleActualStart', val)
                                  }
                                  label={''}
                                  slotProps={{
                                    textField: {
                                      size: 'small',
                                      fullWidth: true,
                                      error: '',
                                      placeholder: '',
                                      sx: {
                                        backgroundColor: '#fff',
                                        borderRadius: '7px',
                                        '& .MuiInputBase-input': {
                                          fontSize: 11,
                                          fontFamily: 'poppinsSemiBold',
                                          padding: '11px 12px'
                                        },
                                        '& .MuiOutlinedInput-root': {
                                          '& fieldset': {
                                            borderColor: palette.primary.inputborder,
                                            borderWidth: '1.5px',
                                            borderRadius: '7px'
                                          },
                                          '&:hover fieldset': {
                                            borderColor: palette.primary.inputborder
                                          },
                                          '&.Mui-focused fieldset': {
                                            borderColor: palette.primary.inputborder
                                          }
                                        }
                                      }
                                    },
                                    openPickerIcon: {
                                      sx: { fontSize: 18, color: palette.primary.dropdownArrow }
                                    }
                                  }}
                                  slots={{
                                    openPickerIcon: CalendarMonthIcon
                                  }}
                                />
                              </DemoContainer>
                            </LocalizationProvider>
                          )}
                        </Box>
                      </Grid>

                      {/* PROJECTED COMPLETION */}
                      <Grid item xs={12} sm={6} md={6} sx={{ mb: { xs: 2, sm: 2 } }}>
                        <Typography
                          sx={{
                            fontSize: 12,
                            marginBottom: '-4px',
                            fontFamily: 'poppinsMedium',
                            color: palette.primary.titledark
                          }}
                        >
                          {'Projected completion'}
                        </Typography>
                        <Box
                          sx={{
                            minHeight: 38,
                            display: 'flex',
                            alignItems: 'center',
                            fontFamily: 'poppinsSemiBold',
                            fontSize: 15,
                            color: palette.primary.titledark
                          }}
                        >
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DemoContainer components={['DatePicker', 'DatePicker', 'DatePicker']}>
                              <DatePicker
                                value={form.scheduleProjectedCompletion}
                                onChange={(val) =>
                                  handleScheduleDateChange('scheduleProjectedCompletion', val)
                                }
                                label={''}
                                slotProps={{
                                  textField: {
                                    size: 'small',
                                    fullWidth: true,
                                    error: Boolean(projectedCompletionError),
                                    helperText: projectedCompletionError
                                      ? 'Completion date must be on or later than start date'
                                      : '',
                                    placeholder: '',
                                    sx: {
                                      backgroundColor: '#fff',
                                      borderRadius: '7px',
                                      '& .MuiInputBase-input': {
                                        fontSize: 11,
                                        fontFamily: 'poppinsSemiBold',
                                        padding: '11px 12px'
                                      },
                                      '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                          borderColor: palette.primary.inputborder,
                                          borderWidth: '1.5px',
                                          borderRadius: '7px'
                                        },
                                        '&:hover fieldset': {
                                          borderColor: palette.primary.inputborder
                                        },
                                        '&.Mui-focused fieldset': {
                                          borderColor: palette.primary.inputborder
                                        }
                                      }
                                    }
                                  },
                                  openPickerIcon: {
                                    sx: { fontSize: 18, color: palette.primary.dropdownArrow }
                                  }
                                }}
                                slots={{
                                  openPickerIcon: CalendarMonthIcon
                                }}
                              />
                            </DemoContainer>
                          </LocalizationProvider>
                        </Box>
                      </Grid>

                      {/* ACTUAL COMPLETION */}
                      <Grid item xs={12} sm={6} md={6} sx={{ mb: { xs: 2, sm: 2 } }}>
                        <Typography
                          sx={{
                            fontSize: 12,
                            marginBottom: '-4px',
                            fontFamily: 'poppinsMedium',
                            color: palette.primary.titledark
                          }}
                        >
                          {'Actual completion'}
                        </Typography>
                        <Box
                          sx={{
                            minHeight: 40,
                            display: 'flex',
                            alignItems: 'center',
                            fontFamily: 'poppinsSemiBold',
                            fontSize: 15,
                            color: palette.primary.titledark,
                            paddingTop: form.updateActualDates ? 1.5 : 0
                          }}
                        >
                          {form.updateActualDates ? (
                            '--'
                          ) : (
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <DemoContainer
                                components={['DatePicker', 'DatePicker', 'DatePicker']}
                              >
                                <DatePicker
                                  value={form.scheduleActualCompletion}
                                  onChange={(val) =>
                                    handleScheduleDateChange('scheduleActualCompletion', val)
                                  }
                                  label={''}
                                  slotProps={{
                                    textField: {
                                      size: 'small',
                                      fullWidth: true,
                                      error: Boolean(actualCompletionError),
                                      helperText: actualCompletionError
                                        ? 'Completion date must be on or later than start date'
                                        : '',
                                      placeholder: '',
                                      sx: {
                                        backgroundColor: '#fff',
                                        borderRadius: '7px',
                                        '& .MuiInputBase-input': {
                                          fontSize: 11,
                                          fontFamily: 'poppinsSemiBold',
                                          padding: '11px 12px'
                                        },
                                        '& .MuiOutlinedInput-root': {
                                          '& fieldset': {
                                            borderColor: palette.primary.inputborder,
                                            borderWidth: '1.5px',
                                            borderRadius: '7px'
                                          },
                                          '&:hover fieldset': {
                                            borderColor: palette.primary.inputborder
                                          },
                                          '&.Mui-focused fieldset': {
                                            borderColor: palette.primary.inputborder
                                          }
                                        }
                                      }
                                    },
                                    openPickerIcon: {
                                      sx: { fontSize: 18, color: palette.primary.dropdownArrow }
                                    }
                                  }}
                                  slots={{
                                    openPickerIcon: CalendarMonthIcon
                                  }}
                                />
                              </DemoContainer>
                            </LocalizationProvider>
                          )}
                        </Box>
                      </Grid>
                    </Grid>

                    {/* CHECKBOX: Update actual dates based on Schedule */}
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={form.updateActualDates}
                          onChange={handleCheckboxChange}
                          color="primary"
                          size="small"
                        />
                      }
                      label={
                        <span
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            fontFamily: 'poppinsRegular',
                            fontSize: 12
                          }}
                        >
                          Update actual dates based on Schedule
                          <Tooltip title="Actual start and actual completion will be synced from the first and last schedule items on this job">
                            <IconButton size="small" sx={{ ml: 0.5 }}>
                              <Iconify
                                icon={'material-symbols:info-rounded'}
                                width={18}
                                height={18}
                                style={{ cursor: 'pointer', color: palette.primary.main }}
                              />
                            </IconButton>
                          </Tooltip>
                        </span>
                      }
                      sx={{ mt: actualCompletionError || projectedCompletionError ? -2 : 0, mb: 1 }}
                    />

                    {/* SCHEDULE COLOR */}
                    <Typography sx={{ fontSize: 12, fontFamily: 'poppinsMedium', mb: 1 }}>
                      Schedule color
                    </Typography>
                    <Autocomplete
                      options={colorList.map((val) => ({
                        value: val?.id,
                        name: val?.name,
                        color: val?.code
                      }))}
                      getOptionLabel={(option) => option?.name || ''}
                      value={form.scheduleColor || null}
                      onChange={handleScheduleColorAutocomplete}
                      isOptionEqualToValue={(option, value) => option.value === value.value}
                      popupIcon={
                        <KeyboardArrowDownIcon
                          sx={{
                            color: palette.primary.dropdownArrow,
                            fontSize: 20,
                            marginRight: 0.8
                          }}
                        />
                      }
                      renderOption={(props, option) => (
                        <Box
                          component="li"
                          {...props}
                          key={option.value}
                          sx={{ display: 'flex', alignItems: 'center' }}
                        >
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              background: option.color,
                              mr: 1.5
                            }}
                          />
                          <span style={{ fontFamily: 'poppinsRegular', fontSize: 12 }}>
                            {option.name}
                          </span>
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder=""
                          error={scheduleColorError}
                          helperText={scheduleColorError ? 'Must select a schedule color' : ''}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: form.scheduleColor ? (
                              <Box
                                sx={{
                                  width: 22,
                                  height: 22,
                                  borderRadius: '50%',
                                  background: form.scheduleColor.color,
                                  mr: 1,
                                  ml: 0.5
                                }}
                              />
                            ) : null
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '6px',
                              fontFamily: 'poppinsRegular',
                              fontSize: 12,
                              color: palette.primary.titledark,
                              height: 40,
                              borderWidth: 1.5,
                              // margin: margin,
                              backgroundColor: 'transparent',
                              '& input:-webkit-autofill': {
                                boxShadow: '0 0 0 1000px #fff inset !important',
                                WebkitTextFillColor: palette.primary.titledark,
                                transition: 'background-color 5000s ease-in-out 0s'
                              },
                              '& fieldset': {
                                borderColor: palette.primary.inputborder,
                                borderWidth: '1.5px'
                              },
                              '&:hover fieldset': {
                                borderColor: palette.primary.inputborder
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: palette.primary.inputborder,
                                borderWidth: '1.5px'
                              }
                            }
                          }}
                        />
                      )}
                      sx={{
                        mb: 2,
                        backgroundColor: '#fff'
                      }}
                    />

                    {/* WORK DAYS */}
                    <ProjectManagerDropdown
                      label="Work days"
                      name="workDays"
                      value={form.workDays}
                      onChange={handleWorkDaysDropdownChange}
                      options={workDaysList.map((val) => ({
                        value: val?.id,
                        name: val?.name
                      }))}
                      error={workDaysError}
                      helperText={workDaysError ? 'Must select at least one workday' : ''}
                      placeholder=""
                      backgroundColor="#fff"
                      fontFamily="poppinsRegular"
                      marginBottom="1rem"
                      labelFontMedium
                    />
                  </Grid>

                  {/* RIGHT SIDE */}
                  <Grid item xs={12} md={5} spacing={2}>
                    {/* STATUS */}
                    <Grid item xs={12} md={3}>
                      <CustomDropdown
                        name={'status'}
                        label="Status"
                        marginBottom={'0rem'}
                        value={form.status}
                        height={40}
                        onChange={handleChange}
                        options={projectStatusList.map((val) => ({
                          value: val?.id,
                          label: val?.name
                        }))}
                        backgroundColor="#fff"
                        labelFontMedium
                      />
                    </Grid>

                    {/* CONTRACT PRICE */}
                    <Grid item xs={12} md={6} sx={{ mt: 2 }}>
                      <InputField
                        name="amount"
                        label={'Contract Price'}
                        height={38}
                        backgroundColor={'#fff'}
                        marginBottom={'0rem'}
                        type="number"
                        placeholder=""
                        fontFamily="poppinsRegular"
                        value={form.amount}
                        onChange={handleNumberChange}
                        labelFontMedium
                      />
                    </Grid>

                    {/* PROJECT MANAGERS */}
                    <Grid item xs={12} md={12} sx={{ mt: 2 }}>
                      <ProjectManagerDropdown
                        label="Project Managers"
                        name="projectManagers"
                        value={selectedManagers}
                        onChange={(e) => setSelectedManagers(e.target.value)}
                        showNameTag={true}
                        options={projectManagerList.map((val) => ({
                          value: val?.id,
                          name: val?.manager_name,
                          code: val?.manager_name
                        }))}
                        error={false}
                        helperText=""
                        placeholder=""
                        marginBottom="0"
                        labelFontMedium
                      />

                      {selectedManagers.length !== 0 && (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={form.notifyProjects}
                              onChange={handleCheckboxChangeNotify}
                              color="primary"
                              size="small"
                            />
                          }
                          label={
                            <span
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                fontFamily: 'poppinsRegular',
                                fontSize: 12
                              }}
                            >
                              Notify new project managers on save
                              <Tooltip title="Users who do not have access based on their role will not receive a notification">
                                <IconButton size="small" sx={{ ml: 0.5 }}>
                                  <Iconify
                                    icon={'material-symbols:info-rounded'}
                                    width={18}
                                    height={18}
                                    style={{ cursor: 'pointer', color: palette.primary.main }}
                                  />
                                </IconButton>
                              </Tooltip>
                            </span>
                          }
                        />
                      )}
                    </Grid>

                    {/* Square Feet  */}
                    <Grid item xs={12} md={12} sx={{ mt: selectedManagers.length == 0 ? 2 : 1 }}>
                      <InputField
                        name="squareFeet"
                        label={'Square feet'}
                        height={38}
                        backgroundColor={'#fff'}
                        marginBottom={'0rem'}
                        type="number"
                        placeholder=""
                        fontFamily="poppinsRegular"
                        value={form.squareFeet}
                        onChange={handleNumberChange}
                        labelFontMedium
                      />
                    </Grid>

                    {/* Permit Number */}
                    <Grid item xs={12} md={12} sx={{ mt: 2 }}>
                      <InputField
                        name="permitNumber"
                        label={'Permit number'}
                        height={38}
                        backgroundColor={'#fff'}
                        marginBottom={'0rem'}
                        type="number"
                        placeholder=""
                        fontFamily="poppinsRegular"
                        value={form.permitNumber}
                        onChange={handleNumberChange}
                        labelFontMedium
                      />
                    </Grid>

                    {/* Lot info */}
                    <Grid item xs={12} md={12} sx={{ mt: 2 }}>
                      <MultilineInputField
                        name="lotInfo"
                        label={'Lot info'}
                        height={70}
                        backgroundColor={'#fff'}
                        fontSize={12}
                        type="text"
                        marginBottom={'1rem'}
                        placeholder=""
                        fontFamily="poppinsRegular"
                        value={form.lotInfo}
                        onChange={handleLotInfoChange}
                        rows={3}
                        maxRows={8}
                        labelFontMedium
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {/* NOTES SECTION */}
                <Typography
                  sx={{
                    fontSize: 20,
                    fontFamily: 'poppinsSemiBold',
                    mt: 1,
                    mb: 2,
                    borderBottom: 1,
                    borderColor: '#e0e0e0',
                    pb: 1
                  }}
                >
                  Notes
                </Typography>

                <MultilineInputField
                  name="notesInternal"
                  label={'Notes for internal users'}
                  backgroundColor={'#fff'}
                  fontSize={12}
                  type="text"
                  marginBottom={'1rem'}
                  placeholder=""
                  fontFamily="poppinsRegular"
                  value={form.notesInternal}
                  onChange={handleChange}
                  labelFontMedium
                />

                <MultilineInputField
                  name="notesVendors"
                  label={'Notes for subs/vendors'}
                  backgroundColor={'#fff'}
                  fontSize={12}
                  type="text"
                  marginBottom={'1rem'}
                  placeholder=""
                  fontFamily="poppinsRegular"
                  value={form.notesVendors}
                  onChange={handleChange}
                  labelFontMedium
                />

                {/* Custom fields (7) */}
                <Box
                  onClick={() => setShowCustomFields((prev) => !prev)}
                  sx={{
                    mt: 3,
                    mb: 2,
                    borderBottom: 1,
                    borderColor: '#e0e0e0',
                    pb: 1,
                    cursor: 'pointer',
                    userSelect: 'none',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <Iconify
                    icon={showCustomFields ? 'bxs:down-arrow' : 'bxs:right-arrow'}
                    style={{ marginRight: 8, fontSize: 14 }}
                  />
                  <Typography
                    sx={{
                      fontSize: 20,
                      fontFamily: 'poppinsSemiBold'
                    }}
                  >
                    Custom fields (7)
                  </Typography>
                </Box>

                <Collapse in={showCustomFields} timeout={350}>
                  <Grid xs={12} md={5} container sx={{ mb: 3 }}>
                    {/* Claim Information */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <Typography sx={{ fontSize: 12, fontFamily: 'poppinsMedium' }}>
                        Claim Information
                      </Typography>
                      <Tooltip title="Enter claim information or edit as applicable to keep up to date">
                        <IconButton size="small">
                          <Iconify
                            icon={'material-symbols:info-rounded'}
                            width={18}
                            height={18}
                            style={{ cursor: 'pointer', color: palette.primary.main }}
                          />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <MultilineInputField
                      name="claimInformation"
                      label={''}
                      backgroundColor={'#fff'}
                      fontSize={12}
                      type="text"
                      marginBottom={'1rem'}
                      placeholder=""
                      fontFamily="poppinsRegular"
                      value={form.claimInformation}
                      onChange={handleChange}
                      labelFontMedium
                    />

                    {/* Policy Documents */}
                    <Grid item xs={12}>
                      <Typography sx={{ fontSize: 12, fontFamily: 'poppinsMedium', mb: 0.5 }}>
                        Policy Documents
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          mb: 0.5,
                          mb: form.policyDocument ? 0 : 1.5
                        }}
                      >
                        <Button
                          variant="outlined"
                          component="label"
                          sx={{
                            fontFamily: 'poppinsRegular',
                            textTransform: 'none',
                            borderRadius: 0.6,
                            mr: 2,
                            height: 30,
                            minWidth: 120,
                            fontSize: 12
                          }}
                          startIcon={
                            <Iconify icon="material-symbols:upload" width={20} height={20} />
                          }
                        >
                          {form.policyDocument ? 'Replace File' : 'Choose File'}
                          <input
                            type="file"
                            hidden
                            onChange={handlePolicyDocumentChange}
                            accept=".pdf,.doc,.docx,.jpg,.png,.p8"
                          />
                        </Button>
                      </Box>
                      {policyDocuments.map((doc) => {
                        const isUploading = uploadingFiles.includes(doc.name);
                        return (
                          <Box
                            key={doc.id}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              backgroundColor: palette.primary.lighterMain,
                              px: 1,
                              py: 0.3,
                              mb: 1,
                              width: 'fit-content'
                            }}
                          >
                            <Iconify
                              icon="octicon:link-16"
                              width={15}
                              height={15}
                              style={{ marginRight: 4, color: palette.primary.main }}
                            />
                            {!isUploading && (
                              <>
                                <Link
                                  href={doc.url ? doc.url : '#'}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{
                                    fontSize: 13,
                                    fontFamily: 'poppinsMedium',
                                    color: palette.primary.main,
                                    maxWidth: 200,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    cursor: doc.url ? 'pointer' : 'not-allowed'
                                  }}
                                  title={doc.name}
                                  onClick={(e) => {
                                    if (!doc.url) e.preventDefault();
                                  }}
                                >
                                  {doc.name}
                                </Link>
                              </>
                            )}
                            {isUploading && (
                              <Box sx={{ ml: 0.5 }}>
                                <span
                                  style={{
                                    fontSize: 13,
                                    color: palette.primary.main,
                                    fontFamily: 'poppinsMedium'
                                  }}
                                >
                                  Uploading...
                                </span>
                              </Box>
                            )}
                            {!isUploading && (
                              <IconButton
                                size="small"
                                sx={{ ml: 1 }}
                                onClick={() => handleDeletePolicyDocument(doc.id)}
                              >
                                <Iconify
                                  icon="material-symbols:delete-rounded"
                                  width={16}
                                  height={16}
                                  color={palette.primary.deleteButtonTitle}
                                />
                              </IconButton>
                            )}
                          </Box>
                        );
                      })}
                    </Grid>

                    {/* Permit Parcel ID */}
                    <Grid item xs={12}>
                      <InputField
                        name="permitParcelId"
                        label="Permit Parcel ID"
                        backgroundColor="#fff"
                        value={form.permitParcelId}
                        onChange={handleChange}
                        labelFontMedium
                      />
                    </Grid>

                    {/* Contractor License */}
                    <Grid item xs={12}>
                      <InputField
                        name="contractorLicense"
                        label="Contractor License"
                        backgroundColor="#fff"
                        value={form.contractorLicense}
                        onChange={handleChange}
                        labelFontMedium
                      />
                    </Grid>

                    {/* FL GIS LINK */}
                    <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center' }}>
                      <InputField
                        name="flGisLink"
                        label="FL GIS LINK"
                        backgroundColor="#fff"
                        value={form.flGisLink}
                        onChange={handleChange}
                        labelFontMedium
                        styleMainContainer={{ flex: 1 }}
                      />
                      <LoadingButtonWrapper
                        title="View"
                        style={{
                          marginTop: 6,
                          marginLeft: 10,
                          width: 70,
                          borderRadius: 5,
                          letterSpacing: 0.5,
                          boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.1)',
                          transition: 'width 0.3s ease'
                        }}
                        height={38}
                        fontSize={12}
                        fontFamily="poppinsLight"
                        disabled={!form.flGisLink}
                        onClick={() => window.open(form.flGisLink, '_blank')}
                      />
                    </Grid>

                    {/* Local Supplier Link */}
                    <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center' }}>
                      <InputField
                        name="localSupplierLink"
                        label=" Local Supplier Link"
                        backgroundColor="#fff"
                        value={form.localSupplierLink}
                        onChange={handleChange}
                        labelFontMedium
                        styleMainContainer={{ flex: 1 }}
                      />
                      <LoadingButtonWrapper
                        title="View"
                        style={{
                          marginTop: 6,
                          marginLeft: 10,
                          width: 70,
                          borderRadius: 5,
                          letterSpacing: 0.5,
                          boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.1)',
                          transition: 'width 0.3s ease'
                        }}
                        height={38}
                        fontSize={12}
                        fontFamily="poppinsLight"
                        disabled={!form.localSupplierLink}
                        onClick={() => window.open(form.localSupplierLink, '_blank')}
                      />
                    </Grid>

                    {/* Property Special Notes */}
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <Typography sx={{ fontSize: 12, fontFamily: 'poppinsMedium' }}>
                          # Property Special Notes
                        </Typography>
                      </Box>
                      <MultilineInputField
                        name="propertySpecialNotes"
                        label={''}
                        backgroundColor={'#fff'}
                        fontSize={12}
                        type="text"
                        marginBottom={'1rem'}
                        placeholder=""
                        fontFamily="poppinsRegular"
                        value={form.propertySpecialNotes}
                        onChange={handleChange}
                      />
                    </Grid>
                  </Grid>
                </Collapse>
              </form>
            </FormContainer>
          )}

          {/* Save Button */}
          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              left: 0,
              width: '100%',
              zIndex: 10,
              background: '#fff',
              borderTop: showFullForm && '1px solid #e0e0e0',
              boxShadow: showFullForm && '0px -2px 8px rgba(0,0,0,0.04)',
              pt: 2,
              px: { xs: 2, md: 3 },
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center'
            }}
          >
            <LoadingButtonWrapper
              title="Save"
              loading={saveProjectLoading}
              style={{
                width: saveProjectLoading ? 130 : 100,
                paddingLeft: 10,
                paddingRight: 10,
                borderRadius: 5,
                letterSpacing: 0.5,
                boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.1)',
                transition: 'width 0.3s ease'
              }}
              height={30}
              fontSize={12}
              fontFamily="poppinsLight"
              onClick={handleSave}
            />
          </Box>

          {/* JOB GROUP ADD/EDIT MODAL */}
          <JobGroupEditAdd
            open={jobGroupModal.open}
            mode={jobGroupModal.mode}
            initialValue={jobGroupModal.value}
            onSave={handleJobGroupSave}
            onClose={() => setJobGroupModal({ ...jobGroupModal, open: false })}
            // onDelete={...}
            saveLoading={saveLoading}
          />
        </>
      )}
    </Box>
  );
}
